<script lang="ts">
  /**
   * 並べ替え（card-schema.md §3.3、judge.md §4.2、components.md §1.4）。
   * すべての行を「並べた行」に入れて出題し、使わない行は「使わない行」へ移す。
   * キーボード: ↑↓ で行を選ぶ、Shift+↑↓ で動かす、Space で「使わない行」と行き来する。マウスはドラッグ。
   */
  import { tick } from 'svelte';
  import { gradeParsons } from '../../lib/judge';
  import type { ParsonsCard } from '../../lib/cards';
  import CardShell from '../CardShell.svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import type { GradeResult } from '../types';
  import type { ParsonsAnswer } from './answer';
  import { keysFromLines, moveItem, parsonsLines, parsonsMessage } from './parsons';

  interface Props {
    card: ParsonsCard;
    index: number;
    total: number;
    box?: number;
    result?: GradeResult | undefined;
    answer?: ParsonsAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    ongrade?: ((result: GradeResult, answer: ParsonsAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（level は開いた段階、1 始まり） */
    onhint?: ((level: number) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer: savedAnswer,
    showUseCase = false,
    globalKeys = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
  }: Props = $props();

  type ListName = 'used' | 'unused';
  const uid = $props.id();
  const hasDistractors = $derived((card.distractors?.length ?? 0) > 0);
  const pool = $derived(parsonsLines(card));
  const textOf = (key: number) => pool.find((l) => l.key === key)?.text ?? '';

  let used = $state<number[]>([]);
  let unused = $state<number[]>([]);
  let active = $state<{ list: ListName; key: number } | null>(null);
  let announcement = $state('');

  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    if (savedAnswer) {
      used = keysFromLines(pool, savedAnswer.lines);
      unused = pool.map((l) => l.key).filter((k) => !used.includes(k));
    } else {
      used = pool.map((l) => l.key);
      unused = [];
    }
    active = null;
    announcement = '';
  });

  const graded = $derived(result !== undefined);
  let touched = $state(false);
  const gradeState = $derived(graded ? 'graded' : touched ? 'answering' : 'idle');

  const listOf = (name: ListName) => (name === 'used' ? used : unused);
  function setList(name: ListName, keys: number[]) {
    if (name === 'used') used = keys;
    else unused = keys;
    touched = true;
  }

  let root: HTMLElement | undefined = $state();
  async function focusLine(list: ListName, key: number) {
    active = { list, key };
    await tick();
    root?.querySelector<HTMLElement>(`[data-list="${list}"] [data-key="${key}"]`)?.focus();
  }

  function moveWithin(list: ListName, key: number, delta: number) {
    const keys = listOf(list);
    const from = keys.indexOf(key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= keys.length) return;
    setList(list, moveItem(keys, from, to));
    announcement = `${to + 1} 行目に移動しました（全 ${keys.length} 行）`;
    void focusLine(list, key);
  }

  function transfer(from: ListName, key: number, at?: number) {
    const to: ListName = from === 'used' ? 'unused' : 'used';
    const src = listOf(from);
    const pos = src.indexOf(key);
    if (pos < 0) return;
    setList(
      from,
      src.filter((k) => k !== key),
    );
    const dst = listOf(to).filter((k) => k !== key);
    const insertAt = at ?? dst.length;
    dst.splice(insertAt, 0, key);
    setList(to, dst);
    announcement =
      to === 'unused'
        ? `使わない行に移しました（並べた行は ${used.length} 行）`
        : `${insertAt + 1} 行目に戻しました（全 ${used.length} 行）`;
    return { to, pos };
  }

  function onLineKeydown(e: KeyboardEvent, list: ListName, key: number) {
    if (graded || e.metaKey || e.ctrlKey || e.altKey) return;
    const keys = listOf(list);
    const i = keys.indexOf(key);
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const delta = e.key === 'ArrowUp' ? -1 : 1;
      if (e.shiftKey) {
        moveWithin(list, key, delta);
      } else {
        const next = keys[i + delta];
        if (next !== undefined) void focusLine(list, next);
      }
      return;
    }
    if (e.key === ' ' || e.key === 'Delete' || e.key === 'Backspace') {
      if (!hasDistractors && list === 'used') return;
      e.preventDefault();
      const moved = transfer(list, key);
      if (!moved) return;
      // フォーカスは元の一覧の同じ位置（無ければ移した行）へ
      const rest = listOf(list);
      const stay = rest[Math.min(moved.pos, rest.length - 1)];
      if (stay !== undefined) void focusLine(list, stay);
      else void focusLine(moved.to, key);
    }
  }

  // ---- ドラッグ（マウス） ----
  let dragging = $state<{ list: ListName; key: number } | null>(null);
  let dropAt = $state<{ list: ListName; index: number } | null>(null);

  function onDragStart(e: DragEvent, list: ListName, key: number) {
    if (graded) return;
    dragging = { list, key };
    e.dataTransfer?.setData('text/plain', String(key));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  }

  function onDragOverLine(e: DragEvent, list: ListName, i: number) {
    if (!dragging) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    let index = i;
    if (el instanceof HTMLElement) {
      const r = el.getBoundingClientRect();
      if (e.clientY > r.top + r.height / 2) index = i + 1;
    }
    dropAt = { list, index };
  }

  function onDragOverList(e: DragEvent, list: ListName) {
    if (!dragging) return;
    e.preventDefault();
    if (dropAt?.list !== list) dropAt = { list, index: listOf(list).length };
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    const d = dragging;
    const t = dropAt;
    dragging = null;
    dropAt = null;
    if (!d || !t || graded) return;
    if (d.list === t.list) {
      const keys = listOf(d.list);
      const from = keys.indexOf(d.key);
      const to = t.index > from ? t.index - 1 : t.index;
      if (from === to) return;
      setList(d.list, moveItem(keys, from, to));
      announcement = `${to + 1} 行目に移動しました（全 ${keys.length} 行）`;
    } else {
      transfer(d.list, d.key, t.index);
    }
    active = { list: t.list, key: d.key };
  }

  function onDragEnd() {
    dragging = null;
    dropAt = null;
  }

  function submit() {
    if (graded || used.length === 0) return;
    const lines = used.map(textOf);
    const ok = gradeParsons(card, lines);
    const r = parsonsMessage(card, lines, ok);
    ongrade?.(r, { kind: 'parsons', lines });
  }

  /** 一覧ごとに Tab で止まる行は 1 つ（roving tabindex） */
  const isTabStop = (list: ListName, key: number, i: number) =>
    active?.list === list ? active.key === key : i === 0;
  const describeId = `${uid}-how`;
</script>

{#snippet lineList(list: ListName, keys: number[], label: string)}
  <ol
    class="lines"
    class:empty={keys.length === 0}
    role="listbox"
    aria-label={label}
    aria-describedby={describeId}
    data-list={list}
    data-testid="parsons-{list}"
    ondragover={(e) => onDragOverList(e, list)}
    ondrop={onDrop}
  >
    {#each keys as key, i (key)}
      <li
        class="line"
        class:active={active?.list === list && active.key === key}
        class:dragging={dragging?.key === key}
        class:drop-before={dropAt?.list === list && dropAt.index === i}
        class:drop-after={dropAt?.list === list &&
          dropAt.index === keys.length &&
          i === keys.length - 1}
        role="option"
        aria-selected={active?.list === list && active.key === key}
        tabindex={isTabStop(list, key, i) ? 0 : -1}
        draggable={!graded}
        data-key={key}
        data-testid="line"
        onfocus={() => (active = { list, key })}
        onkeydown={(e) => onLineKeydown(e, list, key)}
        ondragstart={(e) => onDragStart(e, list, key)}
        ondragover={(e) => onDragOverLine(e, list, i)}
        ondragend={onDragEnd}
      >
        {#if list === 'used'}<span class="no num" aria-hidden="true">{i + 1}</span>{/if}
        <code class="text">{textOf(key)}</code>
        {#if !graded}
          <span class="tools">
            {#if list === 'used'}
              <button
                class="btn btn-quiet btn-sm"
                type="button"
                tabindex="-1"
                aria-label="上へ"
                disabled={i === 0}
                onclick={() => moveWithin(list, key, -1)}>↑</button
              >
              <button
                class="btn btn-quiet btn-sm"
                type="button"
                tabindex="-1"
                aria-label="下へ"
                disabled={i === keys.length - 1}
                onclick={() => moveWithin(list, key, 1)}>↓</button
              >
            {/if}
            {#if hasDistractors}
              <button
                class="btn btn-quiet btn-sm"
                type="button"
                tabindex="-1"
                data-testid="transfer"
                onclick={() => transfer(list, key)}>{list === 'used' ? '使わない' : '戻す'}</button
              >
            {/if}
          </span>
        {/if}
      </li>
    {/each}
  </ol>
{/snippet}

<CardShell
  {globalKeys}
  {card}
  {index}
  {total}
  state={gradeState}
  {result}
  {box}
  prompt="次の出力になるように、行を正しい順に並べてください。"
  {showUseCase}
  canSubmit={used.length > 0}
  onsubmit={submit}
  {onnext}
  {onprev}
  {onabort}
  {onhint}
>
  <div class="parsons" bind:this={root} class:graded>
    <div class="io">
      {#if card.stdin}
        <div>
          <h2 class="sub">入力</h2>
          <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
          <pre class="output" tabindex="0" aria-label="入力">{card.stdin.replace(/\n$/, '')}</pre>
        </div>
      {/if}
      {#if card.expected !== undefined}
        <div>
          <h2 class="sub">期待する出力</h2>
          <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
          <pre class="output" tabindex="0" aria-label="期待する出力">{card.expected.replace(
              /\n$/,
              '',
            )}</pre>
        </div>
      {/if}
    </div>
    <p id={describeId} class="how">
      ↑↓ で行を選び、Shift + ↑↓ で動かします。{hasDistractors
        ? 'Space で「使わない行」と行き来します。'
        : ''}ドラッグでも動かせます。
    </p>
    <div>
      <h2 class="sub">並べた行</h2>
      {@render lineList('used', used, '並べた行')}
    </div>
    {#if hasDistractors}
      <div>
        <h2 class="sub">使わない行</h2>
        {@render lineList('unused', unused, '使わない行')}
      </div>
    {/if}
    <p class="visually-hidden" aria-live="polite">{announcement}</p>
  </div>

  {#snippet feedback()}
    {#if result && result.verdict !== 'AC'}
      <div>
        <h3 class="sub">正しい並び</h3>
        <CodeBlock code={card.lines.join('\n')} label="正しい並び" />
      </div>
    {/if}
  {/snippet}
</CardShell>

<style>
  .parsons {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .how {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .io {
    display: grid;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }
  .output {
    margin: 0;
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--surface-code);
    font-family: var(--font-mono);
    font-size: var(--fs-sm);
    white-space: pre-wrap;
  }
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .lines {
    list-style: none;
    margin: 0;
    padding: var(--space-2);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    border: 1px dashed var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--canvas);
    min-height: var(--tap-min);
  }
  .lines.empty::after {
    content: '（なし）';
    color: var(--text-soft);
    font-size: var(--fs-sm);
    padding: var(--space-2);
  }
  .line {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--tap-min);
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
    background: var(--surface-code);
    cursor: grab;
    transition:
      box-shadow var(--dur-fast) var(--ease-out),
      opacity var(--dur-fast) var(--ease-out);
  }
  .graded .line {
    cursor: default;
  }
  .line:focus-visible {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: var(--focus-offset);
  }
  .line.active {
    border-color: var(--accent);
  }
  .line.dragging {
    opacity: 0.5;
    box-shadow: var(--shadow-lift);
  }
  .line.drop-before::before,
  .line.drop-after::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--accent);
    border-radius: var(--radius-pill);
  }
  .line.drop-before::before {
    top: calc(-1 * var(--space-1));
  }
  .line.drop-after::after {
    bottom: calc(-1 * var(--space-1));
  }
  .no {
    flex: none;
    width: 2.5ch;
    text-align: right;
    color: var(--code-gutter);
    font-size: var(--fs-sm);
  }
  .text {
    flex: 1;
    min-width: 0;
    white-space: pre;
    overflow-x: auto;
    background: none;
    font-family: var(--font-mono);
    font-size: var(--fs-code);
    color: var(--syn-text);
  }
  .tools {
    flex: none;
    display: flex;
    gap: var(--space-1);
  }
</style>
